<!-- Header -->
<div
  *ngIf="queryParams$ | async as queryParams"
  class="tw-flex tw-flex-column tw-gap-3 tw-mb-3">
  <!-- First Filter Row -->
  <div class="header-container">
    <mg-form-date-range
      class="date-range-input"
      [value]="{
        start: queryParams.start_date,
        end: queryParams.end_date
      }"
      (valueChange)="rangeChanged($event)">
    </mg-form-date-range>
    <mg-form-select
      class="period-select-input"
      [placeholder]="MSG.FIELD_LABEL_FLEX_PERIOD"
      [labelBackground]="'white'"
      [isClearable]="false"
      [value]="queryParams.period_id || null"
      [options]="periodOptions$ | async"
      (selectionChanged)="setPeriodFilter($any($event))">
    </mg-form-select>
    <mg-filters-form
      appearance="primary"
      title="Filters"
      [showChips]="true"
      [responsive]="true"
      [data]="filtersFormStructure$ | async"
      (state)="updateFilters($event)">
    </mg-filters-form>
  </div>

  <!-- Second Filter Row -->
  <div class="header-container tw-justify-between">
    <mat-button-toggle-group
      name="showMineFilter"
      class="filter-toggle-group"
      [value]="queryParams.show_mine"
      (change)="setShowMineFilter($event.value)">
      <mat-button-toggle value="all">
        <mg-text variant="body-sm-secondary">
          {{ MSG.BUTTON_ALL_ACTIVITIES }}
        </mg-text>
      </mat-button-toggle>
      <mat-button-toggle value="mine">
        <mg-text variant="body-sm-secondary">
          {{ MSG.BUTTON_MY_ACTIVITIES }}
        </mg-text>
      </mat-button-toggle>
    </mat-button-toggle-group>
    <mg-btn
      *ngIf="activePeriod$ | async as activePeriod"
      variant="filled"
      [responsive]="true"
      [disabled]="
        isInPast(activePeriod) || (!activePeriod.enableActivityManagement && !('FLEX_TIME_MANAGE' | hasPermission))
      "
      (pressed)="addActivity(activePeriod.id)">
      {{ MSG.BUTTON_ADD_ACTIVITY }}
    </mg-btn>
  </div>

  <ng-container *ngIf="activePeriod$ | async as activePeriod">
    <!-- Deadline Alert -->
    <mg-system-alert
      *ngIf="activePeriod.teacherRegistrationDeadline && (showAlert$ | async) && !('FLEX_TIME_MANAGE' | hasPermission)"
      class="tw-w-full"
      [type]="(activePeriod.teacherRegistrationDeadline | isInPast) ? 'warning' : 'default'"
      [dismissable]="activePeriod.teacherRegistrationDeadline | isInPast"
      [message]="
        (activePeriod.teacherRegistrationDeadline | isInPast)
          ? MSG.ALERT_AFTER_DEADLINE
          : MSG.ALERT_BEFORE_DEADLINE + ' ' + (activePeriod.teacherRegistrationDeadline | dayjs: 'ddd MMM DD  - h:mma')
      "
      (dismissSnackbar)="onDismissAlert()">
    </mg-system-alert>

    <!-- Period Data -->
    <div class="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
      <mg-text
        variant="header-md-secondary"
        [numberOfLines]="2">
        {{ activePeriod.title }}
      </mg-text>
      <div class="separator"></div>
      <mg-text variant="header-sm">
        {{ activePeriod.date | date: 'EEEE, MMM d' }}
      </mg-text>
      <div class="separator"></div>
      <mg-text variant="header-sm">
        {{ activePeriod | formatTimeRange }}
      </mg-text>
    </div>
  </ng-container>
</div>

<!-- Table -->
<mat-table
  matSort
  class="list-table flexible-mobile-layout"
  matSortActive="lastName"
  matSortDirection="desc"
  [dataSource]="dataSource"
  [trackBy]="trackById">
  <!-- Asset Column -->
  <ng-container matColumnDef="asset">
    <mat-header-cell *matHeaderCellDef>
      {{ MSG.COLUMN_LABEL_ASSET }}
    </mat-header-cell>
    <mat-cell
      *matCellDef="let item"
      [ngClass]="{
        'mat-column-image': item.flexTimeActivity?.bannerImage || item.flexTimeActivity?.imagePath
      }">
      <mg-ftm-activity-img
        [bannerImage]="item.flexTimeActivity?.bannerImage"
        [imagePath]="item.flexTimeActivity?.imagePath"></mg-ftm-activity-img>
    </mat-cell>
  </ng-container>

  <!-- Title Column -->
  <ng-container matColumnDef="title">
    <mat-header-cell
      *matHeaderCellDef
      mat-sort-header
      mgSortHeader
      class="w-sm-flex"
      [activeColumnKey]="activeSortColumn$ | async"
      (click)="selectActiveColumn('title')">
      <mg-text
        class="column-header"
        variant="body-xs-secondary"
        color="grey-300">
        {{ MSG.COLUMN_LABEL_TITLE }}
      </mg-text>
    </mat-header-cell>
    <mat-cell
      *matCellDef="let item"
      class="w-sm-flex">
      <mg-text
        variant="body-sm"
        textAlign="left"
        matTooltipClass="mg-tooltip"
        [matTooltip]="item.flexTimeActivity?.name"
        [numberOfLines]="2">
        {{ item.flexTimeActivity?.name }}
      </mg-text>
    </mat-cell>
  </ng-container>

  <!-- Teachers Column -->
  <ng-container
    matColumnDef="teachers"
    class="text-right">
    <mat-header-cell *matHeaderCellDef> </mat-header-cell>
    <mat-cell
      *matCellDef="let item"
      class="text-right">
      <mg-icon
        matTooltipClass="mg-tooltip"
        [matTooltip]="
          (item.flexTimeActivity | additionalTeachers) === null
            ? ''
            : 'Activity teachers:\n' + (item.flexTimeActivity | additionalTeachers)
        "
        [iconName]="(item.flexTimeActivity | additionalTeachers) === null ? 'mg-person' : 'mg-group-menu'">
      </mg-icon>
    </mat-cell>
  </ng-container>

  <!-- First Name Column -->
  <ng-container matColumnDef="firstName">
    <mat-header-cell
      *matHeaderCellDef
      mat-sort-header
      mgSortHeader
      class="w-sm-flex"
      [activeColumnKey]="activeSortColumn$ | async"
      (click)="selectActiveColumn('firstName')">
      <mg-text
        class="column-header"
        variant="body-xs-secondary"
        color="grey-300">
        {{ MSG.COLUMN_LABEL_FIRST_NAME }}
      </mg-text>
    </mat-header-cell>
    <mat-cell
      *matCellDef="let item"
      class="w-sm-flex">
      <mg-text
        variant="body-sm"
        textAlign="left"
        matTooltipClass="mg-tooltip"
        [matTooltip]="item.flexTimeActivity?.createdByPerson?.firstName"
        [numberOfLines]="2">
        {{ item.flexTimeActivity?.createdByPerson?.firstName }}
      </mg-text>
    </mat-cell>
  </ng-container>

  <!-- Last Name Column -->
  <ng-container matColumnDef="lastName">
    <mat-header-cell
      *matHeaderCellDef
      mat-sort-header
      mgSortHeader
      class="w-sm-flex"
      [activeColumnKey]="activeSortColumn$ | async"
      (click)="selectActiveColumn('lastName')">
      <mg-text
        class="column-header"
        variant="body-xs-secondary"
        color="grey-300">
        {{ MSG.COLUMN_LABEL_LAST_NAME }}
      </mg-text>
    </mat-header-cell>
    <mat-cell
      *matCellDef="let item"
      class="w-sm-flex">
      <mg-text
        variant="body-sm"
        textAlign="left"
        matTooltipClass="mg-tooltip"
        [matTooltip]="item.flexTimeActivity?.createdByPerson?.lastName"
        [numberOfLines]="2">
        {{ item.flexTimeActivity?.createdByPerson?.lastName }}
      </mg-text>
    </mat-cell>
  </ng-container>

  <!-- Location Column -->
  <ng-container matColumnDef="location">
    <mat-header-cell *matHeaderCellDef>
      <mg-text
        class="column-header"
        variant="body-xs-secondary"
        color="grey-300">
        {{ MSG.COLUMN_LABEL_LOCATION }}
      </mg-text>
    </mat-header-cell>
    <mat-cell *matCellDef="let item">
      <mg-text
        variant="body-sm"
        textAlign="left"
        matTooltipClass="mg-tooltip"
        [matTooltip]="item.flexTimeActivity?.location"
        [numberOfLines]="2">
        {{ item.flexTimeActivity?.location }}
      </mg-text>
    </mat-cell>
  </ng-container>

  <!-- Registered Column -->
  <ng-container matColumnDef="registeredCount">
    <mat-header-cell
      *matHeaderCellDef
      class="text-center">
      <mg-text
        class="column-header"
        variant="body-xs-secondary"
        color="grey-300">
        {{ MSG.COLUMN_LABEL_STUDENTS }}
      </mg-text>
    </mat-header-cell>
    <mat-cell
      *matCellDef="let item"
      class="text-center">
      <ng-container
        [ngTemplateOutlet]="assignBtnTemplate"
        [ngTemplateOutletContext]="{ $implicit: item }">
      </ng-container>
    </mat-cell>
  </ng-container>

  <!-- Checkin Column -->
  <ng-container matColumnDef="checkInAction">
    <mat-header-cell
      *matHeaderCellDef
      class="text-center">
      <mg-text
        class="column-header"
        variant="body-xs-secondary"
        color="grey-300">
        {{ MSG.COLUMN_LABEL_CHECKIN }}
      </mg-text>
    </mat-header-cell>
    <mat-cell
      *matCellDef="let item"
      class="text-center">
      <ng-container
        [ngTemplateOutlet]="checkinBtnTemplate"
        [ngTemplateOutletContext]="{ $implicit: item }">
      </ng-container>
    </mat-cell>
  </ng-container>

  <!-- QR Code Column -->
  <ng-container matColumnDef="qrCode">
    <mat-header-cell *matHeaderCellDef></mat-header-cell>
    <mat-cell
      *matCellDef="let item"
      class="text-center">
      <ng-container
        [ngTemplateOutlet]="qrCodeBtnTemplate"
        [ngTemplateOutletContext]="{ $implicit: item }">
      </ng-container>
    </mat-cell>
  </ng-container>

  <!-- Remove Column -->
  <ng-container matColumnDef="remove">
    <mat-header-cell *matHeaderCellDef> </mat-header-cell>
    <mat-cell *matCellDef="let item">
      <ng-container
        [ngTemplateOutlet]="removeBtnTemplate"
        [ngTemplateOutletContext]="{ $implicit: item }">
      </ng-container>
    </mat-cell>
  </ng-container>

  <!-- Mobile Column -->
  <ng-container matColumnDef="mobile">
    <mat-header-cell *matHeaderCellDef></mat-header-cell>
    <mat-cell
      *matCellDef="let item"
      class="mobile-layout">
      <mg-mobile-card [title]="item.flexTimeActivity?.name">
        <div class="tw-flex tw-flex-column tw-gap-1">
          <div class="tw-flex tw-flex-row tw-justify-between">
            <mg-text
              variant="label-md"
              color="surface-alt">
              {{ MSG.COLUMN_LABEL_TEACHER }}
            </mg-text>
            <mg-text variant="label-md">
              {{ item.flexTimeActivity?.createdByPerson?.name }}
            </mg-text>
          </div>
          <div class="tw-flex tw-flex-row tw-justify-between">
            <mg-text
              variant="label-md"
              color="surface-alt">
              {{ MSG.COLUMN_LABEL_LOCATION }}
            </mg-text>
            <mg-text variant="label-md"> {{ item.flexTimeActivity?.location }}</mg-text>
          </div>
          <div class="tw-flex tw-flex-row tw-justify-between">
            <ng-container
              [ngTemplateOutlet]="assignBtnTemplate"
              [ngTemplateOutletContext]="{ $implicit: item }">
            </ng-container>
            <ng-container
              [ngTemplateOutlet]="checkinBtnTemplate"
              [ngTemplateOutletContext]="{ $implicit: item }">
            </ng-container>
            <ng-container
              [ngTemplateOutlet]="qrCodeBtnTemplate"
              [ngTemplateOutletContext]="{ $implicit: item }">
            </ng-container>
            <ng-container
              [ngTemplateOutlet]="removeBtnTemplate"
              [ngTemplateOutletContext]="{ $implicit: item }">
            </ng-container>
          </div>
        </div>
      </mg-mobile-card>
    </mat-cell>
  </ng-container>

  <mat-header-row
    *matHeaderRowDef="displayedColumns$ | async"
    [fxHide]="
      ((isLoadingInstances$ | async) === false && showEmptyState) || ((hasPeriods$ | async) === false && showEmptyState)
    ">
  </mat-header-row>
  <mat-row *matRowDef="let row; columns: displayedColumns$ | async"> </mat-row>
</mat-table>

<!-- Loading -->
<div
  *ngIf="(isLoadingInstances$ | async) && (hasPeriods$ | async)"
  class="loading-container">
  <mg-spinner
    [diameter]="40"
    [thickness]="3">
  </mg-spinner>
</div>

<!-- Pagination -->
<div
  [fxHide]="
    ((isLoadingInstances$ | async) === false && showEmptyState) || ((hasPeriods$ | async) === false && showEmptyState)
  "
  class="tw-mt-3">
  <mg-paginator></mg-paginator>
</div>

<ng-container
  *ngIf="(hasPeriods$ | async) === false && showEmptyState; else noActivitiesTemplate"
  [ngTemplateOutlet]="noPeriodsTemplate">
</ng-container>

<!-- Empty State Template (Activities) -->
<ng-template #noActivitiesTemplate>
  <ng-container *ngIf="(isLoadingInstances$ | async) === false && showEmptyState">
    <mg-empty-state
      class="tw-mt-4"
      [title]="MSG.NO_ACTIVITIES_TITLE"
      [subtitle]="MSG.NO_ACTIVITIES_SUBTITLE">
    </mg-empty-state>
  </ng-container>
</ng-template>

<!-- Empty State Template (Periods) -->
<ng-template #noPeriodsTemplate>
  <mg-empty-state
    class="tw-mt-4"
    [title]="MSG.NO_PERIODS_TITLE"
    [subtitle]="MSG.NO_PERIODS_SUBTITLE">
  </mg-empty-state>
</ng-template>

<!-- Assign Button Template -->
<ng-template
  #assignBtnTemplate
  let-item>
  <mg-btn
    iconSet="minga"
    icon="mg-user-list"
    variant="text"
    size="small"
    [disabled]="
      !isAdmin &&
      (!(item | canManageActivityRegistration) || (item.flexTimePeriod.teacherRegistrationDeadline | isInPast))
    "
    (pressed)="assignActivity(item)">
    {{ item | activityRegistered }}
  </mg-btn>
</ng-template>

<!-- Check in Button Template -->
<ng-template
  #checkinBtnTemplate
  let-item>
  <mg-btn
    iconSet="minga"
    icon="mg-check-in-outlined"
    variant="filled"
    size="small"
    class="checkin-btn"
    (pressed)="activityCheckIn(item)">
    {{ item | activityCheckin }}
  </mg-btn>
</ng-template>

<!-- Remove Button Template -->
<ng-template
  #removeBtnTemplate
  let-item>
  <mg-btn
    icon="mg-delete"
    iconSet="minga"
    variant="icon"
    [disabled]="isAdmin ? false : !(item | canEditActivityInstance) || !item.flexTimePeriod.enableActivityManagement"
    (pressed)="deleteActivityInstance(item)">
  </mg-btn>
</ng-template>

<!-- QR Code Button Template -->
<ng-template
  #qrCodeBtnTemplate
  let-item>
  <mg-btn
    variant="icon"
    iconSet="minga"
    icon="mg-qr-code"
    [tooltip]="item.flexTimeActivity.enableSelfCheckin ? 'View QR Code' : 'QR code disabled'"
    [disabled]="!item.flexTimeActivity.enableSelfCheckin"
    (pressed)="clickViewQrCode(item)">
  </mg-btn>
</ng-template>
